import React from 'react';
import { observer } from 'mobx-react';

// Helpers
import { checkValidity, getURLSegment } from '../../helpers/helpers.js';

// Translations
import translate from '../Translate.js';

// Components
import Step from '../Common/Step';
import EditSection from './EditSection';

// Dependencies
import moment from 'moment';
import { FeatureFlag } from 'react-launch-darkly';

const ExamEdit = observer(class ExamEdit extends React.Component {
  constructor() {
    super();
    this.setStep = this.setStep.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.prevStep = this.prevStep.bind(this);
    this.renderDefault = this.renderDefault.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.complete = this.complete.bind(this);
    this.handlePlatformVersion = this.handlePlatformVersion.bind(this);
  }

  componentWillMount() {
    const { ExamStore, SettingsStore, ProctorStore, CourseStore } = this.props.store;
    const { pathname } = this.props.routing.location;
    const { role } = this.props.store.SessionStore.userData;

    if(role.toLowerCase() === 'learner') {
      this.props.routing.push({
        pathname: 'unauthorized'
      });
    }

    this.state = {
      step: 1,
      examSid: null,
      skippable: false,
      groups: []
    }

    if(pathname.indexOf('/edit') > -1) {
      // Get the exam sid from the URL
      const examSid = getURLSegment(this, 3);

      // Get the exam data with the sid
      this.getExam(examSid);
      this.setState({
        examSid,
        skippable: true
      })
    } else {
      this.resetExam();
    }

    this.getFilteredProctorTypes();

    // Get insitution settings so we can only show allowed permitted items
    SettingsStore.getInstituteSettings(this);

    // Gets proctor groups to determine whether or not to show virtual calc options 
    ProctorStore.getProctorGroups(this)

    // Make sure other exam attachments don’t interfere
    ExamStore.deleteAttachments = [];
    ExamStore.examAttachments = null;

    // Get exams so we know if this is the first exam of the course
    CourseStore.getExams(null, true);
  }

  componentWillUnmount() {
    this.props.store.ExamStore.resetLMSExams();
  }

  getExam(sid) {
    this.props.store.ExamStore.getExam(sid);
  }

  resetExam() {
    const { ExamStore } = this.props.store;
    const { type } = this.props.routing.location.query;
    let examType = 'Online';

    if(type && type === 'written') {
      examType = 'Written'
    }

    ExamStore.resetExam(examType);
  }

  getFilteredProctorTypes() {
    this.props.store.SessionStore.getProctorGroupTypes();
  }

  setStep(e, step) {
    if(e) {
      e.preventDefault();
    }

    this.setState({
      step
    });
  }

  nextStep() {
    // if(this.state.step === translation.steps.length) {
    //   // If we’re on the last step, go back to the exam detail view
    //   const newPathname = pathname.replace('/edit', '').replace('/exams/add', '');
    //   this.props.routing.push({
    //     pathname: newPathname,
    //     search: '?toaster=examSaved'
    //   });
    // } else {
      // Remove submitted class from form
      const form = document.querySelector('form.submitted');
      if(form) {
        form.classList.remove('submitted');
      }

      // Go to the next step
      this.setState({
        step: this.state.step + 1
      });
    // }
  }

  prevStep() {
    this.setState({
      step: this.state.step - 1
    });
  }

  renderDefault() {
    return '';
  }

  handlePlatformVersion(flagValue) {
    const { ExamStore } = this.props.store;
    
    if (flagValue && typeof flagValue === 'number') {
      ExamStore.setPlatformVersion(flagValue);
    }
    
    return null;
  }

  defaultDiff() {
    return '';
  }

  handleInput(name, e, isConfiguration, date, time, isCheckbox, array, skipFormat, categorySid) {
    const { userTimezone } = this.props.store.SessionStore;

    let data = null;
    let value = null;

    if(e) {
      value = e.target.value;
    }
    // The API expects null for virtualCalculatorType
    // if no type is selected, so we need to convert
    // any empty string value to null.
    if(!value && name === 'virtualCalculatorType') {
      value = null;
    }

    // Make sure boolean values are boolean
    if(value === 'true') {
      value = true;
    }
    if(value === 'false') {
      value = false;
    }

    // If we’re updating a date, we need to format it
    if(date) {
      date = moment(date).format('YYYY-MM-DD');
      const timezone = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm').tz(userTimezone).format('Z');
      value = moment(`${date} ${time} ${timezone}`, 'YYYY-MM-DD HH:mm Z').toISOString();
    } else if(date && skipFormat) {
      value = date;
    }

    // If it’s a checkbox, get the checked state
    if(isCheckbox) {
      value = e.target.checked;
    }

    // If the data provided is an array
    if(array) {
      value = array;
    }

    if(isConfiguration) {
      // If it’s a configuration property
      if(categorySid){
        data = {
          configuration: {
              [name]: {
                [categorySid]: value
              }
          }
        }
      } else {
        data = {
          configuration: {
            [name]: value
          }
        }
      }
    } else {
      data = {
        [name]: value
      };
    }

    this.props.store.ExamStore.updateExamData(data, isConfiguration, categorySid);
  }

  handleSubmit(exit) {
    const { translation } = this.props;
    const { CourseStore, ExamStore, SessionStore, SettingsStore, SyncStore } = this.props.store;
    const { exam } = ExamStore;
    const { configuration } = exam;
    let shouldSyncRoster = false;

    // If sync is enabled and this is the first exam for the course toggle sync tallies
    if (SettingsStore.integration.syncEnrollments && CourseStore.exams.length === 0) {
      shouldSyncRoster = true;
    }

    this.setState({
      submitted: null
    });

    // Don’t submit until the last step
    if(exit === true) {
      this.setStep(null, translation.steps.length);
    } else if(this.state.step === translation.steps.length) {
      let callback = (examSid, configurationSid) => {
        this.handleAttachments(examSid, configurationSid, !shouldSyncRoster);
      };

      // Make sure at least 1 proctor type has been chosen
      if(configuration.proctorTypes.length > 0) {
        // Trigger the tally loading view
        SessionStore.tallyLoad = true;

        if(this.state.examSid) {
          // If we’re editing, update the exam
          SessionStore.tallySteps.push('UpdateExam');
          ExamStore.updateExam(this.state.examSid, callback);
        } else {
          // Otherwise post a new exam
          SessionStore.tallySteps.push('CreateExam');
          ExamStore.createExam(callback, this);
        }

        // Add tally step for removed attachments
        if(ExamStore.deleteAttachments.length > 0) {
          SessionStore.tallySteps.push('DeleteExamAttachments');
        }

        // Add tally step for new attachments
        if(
          (exam.type.toLowerCase() === 'online' && ExamStore.examAttachments) ||
          (exam.type.toLowerCase() === 'written' && ExamStore.examFiles)
        ) {
          SessionStore.tallySteps.push('UpdateExamAttachments');
        }

      } else {
        // Let the user know that no proctor types are selected
        const dialogContent = translation.proctor_warning;
        SessionStore.showDialog(dialogContent);
      }

      // Sync course roster if necessary
      if (shouldSyncRoster) {
        SessionStore.tallySteps.push('scheduleSync');
        SessionStore.tallySteps.push('scheduleSyncCheck');
        SessionStore.tallyConfirmClose = true;

        const syncCallback = () => {
          CourseStore.checkLastCourseSync(moment.utc(), 0);
        };

        const failCallback = () => {
          // Send this at the attempt limit to force red check on tally page
          CourseStore.checkLastCourseSync(moment.utc(), 23);
        }
        const syncData = {type:'enrollments', sid: SessionStore.userData.courseSid}
        SyncStore.scheduleSync(syncData, syncCallback, failCallback, true);
      }
    } else {
      this.nextStep();
    }
  }

  handleAttachments(examSid, configurationSid, handleComplete) {
    const { ExamStore } = this.props.store;
    const { exam } = ExamStore;

    // Handle new attachments and deleted attachments
    if(ExamStore.deleteAttachments.length > 0) {
      // New attachments
      let callback = null;

      if(
        ((exam.type.toLowerCase() === 'online' && !ExamStore.examAttachments) ||
        (exam.type.toLowerCase() === 'written' && !ExamStore.examFiles)) 
        && handleComplete) {
        // If there aren’t any to update, run
        // the complete function after uploading
        callback = this.complete;
      } else {
        // Otherwise, run the update endpoint
        callback = () => this.updateAttachments(examSid, configurationSid, handleComplete);
      }

      this.deleteAttachments(examSid, configurationSid, callback)

    } else if(
      (exam.type.toLowerCase() === 'online' && ExamStore.examAttachments) ||
      (exam.type.toLowerCase() === 'written' && ExamStore.examFiles)
    ) {
      this.updateAttachments(examSid, configurationSid, handleComplete);
    } else if (handleComplete) {
      // Otherwise, just finish
      this.complete()
    }
  }

  deleteAttachments(examSid, configurationSid, callback) {
    const { ExamStore } = this.props.store;
    const { exam } = ExamStore;

    // Delete API call
    let deleteData = null;

    // Changes here may need to also be made to ExamException.js, just check it

    if(exam.type.toLowerCase() === 'online') {
      deleteData = {
        attachments: ExamStore.deleteAttachments
      };
    } else {
      deleteData = {
        examFiles: ExamStore.deleteAttachments
      };
    }

    ExamStore.deleteExamAttachments(
      examSid,
      configurationSid,
      deleteData,
      callback
    );
  }

  updateAttachments(examSid, configurationSid, handleComplete) {
    const { ExamStore } = this.props.store;
    const { exam } = ExamStore;

    let uploadData = null;

    if(exam.type.toLowerCase() === 'online') {
      uploadData = {
        attachments: ExamStore.examAttachments
      };
    } else {
      uploadData = {
        examFiles: ExamStore.examFiles
      };
    }

    ExamStore.updateExamAttachments(
      examSid,
      configurationSid,
      uploadData,
      handleComplete ? this.complete : null
    );
  }

  complete() {
    const _this = this;
    const { ExamStore } = this.props.store;

    // Clear out examFiles and examAttachments
    // since they have already been handled
    ExamStore.examAttachments = null;
    ExamStore.examFiles = null;

    // Set a small timeout so the transition
    // from tally loader to next screen
    // isn’t as jarring
    setTimeout(function(){
      // Redirect
      let newPathname = '';
      const segment1 = getURLSegment(_this, 1);
      const segment2 = getURLSegment(_this, 2);


      if(segment2 === 'add') {
        newPathname = `${segment1}/${_this.state.examSid}`;
      } else {
        const examSid = getURLSegment(_this, 3);
        newPathname = `${segment1}/${examSid}`;
      }

      _this.props.routing.push({
        pathname: newPathname,
        search: '?toaster=examSaved'
      });
    }, 500);
  }

  flagAutomatedOnly = (automatedOnly, exam, disabledFields) => {
    return(
      <EditSection
        step={this.state.step}
        nextStep={this.nextStep}
        prevStep={this.prevStep}
        renderDefault={this.renderDefault}
        defaultDiff={this.defaultDiff}
        {...this.props}
        exam={exam}
        handleInput={this.handleInput}
        disabledFields={disabledFields}
        submitted={this.state.submitted}
        automatedOnly={automatedOnly}
        proctorGroups={this.state.groups}
        newExam={this.state.examSid === null}
      />
    )
  }

  render() {
    const { translation } = this.props;
    const { ExamStore, SessionStore, SettingsStore } = this.props.store;
    const { exam, importedLmsExam } = ExamStore;

    if(!SessionStore.loading && SettingsStore.settings && !SessionStore.tallyLoad) {
      let disabledFields = null;

      if(exam.configuration.apiExamId || Object.keys(importedLmsExam).length > 0) {
        disabledFields = [
          'title',
          'duration',
          'password',
          'attempts',
          'examOpenDate',
          'examCloseDate',
          'url',
          'isLimitedScope'
        ]
      }

      const invalidCallback = () => {
        this.setState({
          submitted: true
        });
      }

      return(
        <main tabIndex="0" id="main-content" className="page">
          <header className="page__header">
            <div className="wrap">
              {getURLSegment(this, 2) === 'add' &&
                <h1 className="page__title">{translation.page_title_add}</h1>
              }

              {getURLSegment(this, 2) !== 'add' &&
                <h1 className="page__title">{translation.page_title}</h1>
              }
            </div>
          </header>

          <div className="wrap block-form">
            <Step
              step={this.state.step}
              setStep={this.setStep}
              data={translation.steps}
              skippable={this.state.skippable}
            />

            <form
              className="exam-form panel"
              onSubmit={(e) => checkValidity(e, this.handleSubmit, 'form.exam-form', invalidCallback)}
              noValidate
            >
              <div className="panel__header">
                <h3 className="panel__title"><legend>{translation.steps[this.state.step - 1]}</legend></h3>
              </div>

              <div className="panel__body">
                <FeatureFlag
                  flagKey="virtual-test-center-version"
                  renderFeatureCallback={this.handlePlatformVersion}
                  renderDefaultCallback={this.handlePlatformVersion}
                />

                <FeatureFlag
                  flagKey="automated-only"
                  renderFeatureCallback={automatedOnly => {
                    return this.flagAutomatedOnly(automatedOnly, exam, disabledFields);
                  }}
                  renderDefaultCallback={automatedOnly => {
                    return this.flagAutomatedOnly(automatedOnly, exam, disabledFields);
                  }}
                />

                <div className="panel__actions actions">
                  {this.state.examSid && this.state.step !== translation.steps.length &&
                    <button
                      className="actions__btn actions__btn--secondary actions__btn--offset"
                      type="button"
                      onClick={(e) => checkValidity(e, () => this.handleSubmit(true), 'form.exam-form')}
                    >
                      {translation.save_and_exit}
                    </button>
                  }

                  {this.state.step > 1 &&
                    <button
                      className="actions__btn actions__btn--secondary"
                      type="button"
                      onClick={this.prevStep}
                    >
                      {translation.back}
                    </button>
                  }


                  <button
                    className="actions__btn actions__btn--primary"
                  >
                    {this.state.step !== translation.steps.length &&
                      translation.continue
                    }

                    {this.state.step === translation.steps.length &&
                      translation.confirm
                    }
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      );
    }

    return null;
  }
})

export default translate('ExamEdit')(ExamEdit);



// WEBPACK FOOTER //
// ./src/app/components/ExamEdit/ExamEdit.js